import { isFunction, isIOS } from '@readcloud/common';
import C from 'classnames';
import React, { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CustomSelection } from '../../utils/CustomSelectionLib';
import { getRangeTextContent } from '../../utils/rangeUtils';
import {
  getSelectionStream,
  sanitiseRange,
} from '../../utils/selectionHelpers';

export interface Rect {
  readonly top: number;
  readonly left: number;
  readonly right: number;
  readonly bottom: number;
  x: number;
  y: number;
  height: number;
  width: number;
}
export interface SelectionState {
  startHeight: number;
  endHeight: number;
  clearSelection: () => void;
  nativeRange: Range;
  rangyRange: RangyRange;
  selectedText: string;
  selecting: boolean;
  serializedRange: string;
  referenceElement: HTMLElement;
  location: string;
}
export interface State {
  selectionState?: SelectionState;
  top?: number;
  left?: number;
  width?: number;
  height?: number;
}

const markerSize = 24;
const markerBarWidth = 2.5;

export const Marker = ({
  className,
  end,
  barHeight,
  innerRef,
  ...props
}: {
  end: boolean;
  barHeight: number;
  className: string;
  innerRef: RefObject<HTMLDivElement>;
}) => {
  const markerColor = '#00A9A9';
  return (
    <div
      style={{
        touchAction: 'none',
        zIndex: 999,
        position: 'absolute',
        backgroundColor: markerColor,
        height: markerSize,
        width: markerSize,
        marginLeft: markerSize / -2,
        borderRadius: '50%',
      }}
      className={C('textHighlightSelectionIOS')}
      ref={innerRef}
      {...props}
    >
      <div
        style={{ position: 'relative' }}
        className={C('textHighlightSelectionIOS')}
      >
        <div
          className={C('textHighlightSelectionIOS')}
          style={{
            position: 'absolute',
            top: -barHeight,
            height: barHeight + Math.floor(markerSize / 2) - 2,
            width: markerBarWidth,
            backgroundColor: markerColor,
            left: 0,
            right: 0,
            margin: 'auto',
          }}
        />
      </div>
    </div>
  );
};
/**
 * Use this carefully...
 * It has been hacked to make EPUBs work, and epub's are rendered in an iframe, using css columns.
 * There are many nuances with the position of events/touches, for instance, when you tap+hold, the event generally comes from the epub's content.
 * When you move a marker, it won't come from the epub's content
 */
export const SelectionStream = (props: {
  className?: string;
  markersClassName?: string;
  custom?: boolean;
  element: HTMLElement;
  boundsElement: HTMLElement;
  markersContainer?: HTMLElement;
  selectionCanvasContainer?: HTMLElement;
  pointerEventsBounds?: HTMLElement;
  mapElementBounds?: (el: HTMLElement) => DOMRect;
  window?: Window;
  onSelectionStateChange?: (state?: SelectionState) => void;
  onSelectionChange?: (range?: Range) => void;
  onSelectedTextChange?: (text?: string) => void;
  location: string;
  children?: React.ReactNode | ((params?: SelectionState) => React.ReactNode);
}) => {
  //TODO: Reimplement but without race conditions and with respect to react lifecycles

  const [state, setState] = React.useState<State>({});
  const startMarker = React.useRef<HTMLDivElement>(null);
  const endMarker = React.useRef<HTMLDivElement>(null);

  const propsRef = React.useRef(props);
  propsRef.current = props;
  const customSelectionManagerRef = React.useRef<{ clear: () => void }>();

  const clear = React.useCallback(() => {
    if (customSelectionManagerRef.current) {
      customSelectionManagerRef.current.clear();
    } else {
      // clearGlobalSelection();
    }
  }, []);

  React.useEffect(() => {
    if (props.custom && startMarker.current && startMarker.current) {
      const selectionManager = new CustomSelection(propsRef.current.element, {
        startMarker: startMarker.current!, // For some reason the above check isn't enough to tell typescript that the refs are not null
        endMarker: endMarker.current!, // For some reason the above check isn't enough to tell typescript that the refs are not null
        mapBounds: (el: HTMLElement) => {
          if (propsRef.current.mapElementBounds) {
            return propsRef.current.mapElementBounds(el);
          } else {
            return el.getBoundingClientRect();
          }
        },
        markerShiftY: markerSize,
        boundsElement: propsRef.current.boundsElement,
        selectionColor: '#00A9A980',
        canvasContainer: propsRef.current.selectionCanvasContainer,
        pointerEventsBounds: propsRef.current.pointerEventsBounds,
        onSelectionChange: (selection) => {
          if (selection && !selection.range.collapsed) {
            const rangyRange = selection.range;
            if (propsRef.current.onSelectionChange) {
              propsRef.current.onSelectionChange(rangyRange.nativeRange);
            }
            const selectedText = getRangeTextContent(rangyRange).trim();
            if (propsRef.current.onSelectedTextChange) {
              propsRef.current.onSelectedTextChange(selectedText);
            }
            const sanitisedRange = sanitiseRange(
              rangyRange,
              propsRef.current.element
            );

            if (
              rangyRange &&
              sanitisedRange &&
              selectedText &&
              selectedText.length
            ) {
              const elementBounds = propsRef.current.mapElementBounds
                ? propsRef.current.mapElementBounds(
                    propsRef.current.boundsElement || propsRef.current.element
                  )
                : (
                    propsRef.current.boundsElement || propsRef.current.element
                  ).getBoundingClientRect();

              const rangeBounds = rangyRange.nativeRange
                ? (rangyRange.nativeRange.getBoundingClientRect() as Rect)
                : undefined;
              if (rangeBounds) {
                rangeBounds.y = rangeBounds.y - elementBounds.top;
                rangeBounds.x = rangeBounds.x - elementBounds.left;
              }

              setState((state) => ({
                selectionState: {
                  ...state.selectionState,
                  endHeight: selection.endHeight,
                  startHeight: selection.startHeight,
                  referenceElement:
                    propsRef.current.boundsElement || propsRef.current.element,
                  clearSelection: clear,
                  nativeRange: rangyRange.nativeRange,
                  rangyRange: sanitisedRange.rangyRange,
                  serializedRange: sanitisedRange.serializedRange,
                  selectedText,
                  selecting: selection.selecting,
                  location: propsRef.current.location,
                },
              }));
            }
          } else {
            setState({
              selectionState: undefined,
            });
            if (propsRef.current.onSelectionChange) {
              propsRef.current.onSelectionChange(undefined);
            }
            if (propsRef.current.onSelectedTextChange) {
              propsRef.current.onSelectedTextChange(undefined);
            }
          }
        },
      });

      if (isIOS()) {
        selectionManager.element.style.transform = 'translate3d(0, 0, 0)';
      }

      customSelectionManagerRef.current = selectionManager;

      return () => {
        selectionManager.clear();
        selectionManager.destroy();
        selectionManager.remove();
        customSelectionManagerRef.current = undefined;
      };
    } else if (!props.custom) {
      const sub = getSelectionStream(props.element, props.window).subscribe(
        ({ nativeRange, selectionFinished, rangyRange, serializedRange }) => {
          if (propsRef.current.onSelectionChange) {
            propsRef.current.onSelectionChange(nativeRange);
          }

          const elementBounds = propsRef.current.mapElementBounds
            ? propsRef.current.mapElementBounds(
                propsRef.current.boundsElement || propsRef.current.element
              )
            : (
                propsRef.current.boundsElement || propsRef.current.element
              ).getBoundingClientRect();

          const rangeBounds = nativeRange
            ? (nativeRange.getBoundingClientRect() as Rect)
            : undefined;
          if (rangeBounds) {
            rangeBounds.y = rangeBounds.y - elementBounds.top;
            rangeBounds.x = rangeBounds.x - elementBounds.left;
          }
          const selectedText = nativeRange
            ? getRangeTextContent(nativeRange).trim()
            : undefined;

          if (propsRef.current.onSelectedTextChange) {
            propsRef.current.onSelectedTextChange(selectedText);
          }

          setState({
            selectionState:
              nativeRange &&
              rangyRange &&
              serializedRange &&
              selectedText &&
              selectedText.length
                ? {
                    endHeight: 0,
                    startHeight: 0,
                    clearSelection: clear,
                    nativeRange,
                    selecting: !selectionFinished,
                    rangyRange,
                    serializedRange,
                    selectedText,
                    referenceElement:
                      propsRef.current.boundsElement ||
                      propsRef.current.element,
                    location: propsRef.current.location,
                  }
                : undefined,
          });
        }
      );

      return () => {
        sub.unsubscribe();
      };
    } else {
      return;
    }
  }, [
    props.custom,
    props.element,
    props.window,
    startMarker.current,
    startMarker.current,
  ]);

  const prevState = React.useRef(state.selectionState);

  React.useEffect(() => {
    const selectingChanged =
      (state.selectionState ? state.selectionState.selecting : undefined) !==
      (prevState.current ? prevState.current.selecting : undefined);
    if (props.onSelectionStateChange && selectingChanged) {
      props.onSelectionStateChange(state.selectionState);
    }
    prevState.current = state.selectionState;
  }, [state.selectionState]);

  return (
    <>
      {props.custom ? (
        <>
          {createPortal(
            <>
              <Marker
                className={props.markersClassName || ''}
                barHeight={
                  state.selectionState ? state.selectionState.startHeight : 0
                }
                end={false}
                innerRef={startMarker}
              />
              <Marker
                className={props.markersClassName || ''}
                barHeight={
                  state.selectionState ? state.selectionState.endHeight : 0
                }
                end={true}
                innerRef={endMarker}
              />
            </>,
            props.markersContainer || props.element
          )}
          <div className={C('rc-relative', props.className)}>
            {props.children
              ? isFunction(props.children)
                ? props.children(state.selectionState)
                : props.children
              : null}
          </div>
        </>
      ) : props.children ? (
        isFunction(props.children) ? (
          props.children(state.selectionState)
        ) : (
          props.children
        )
      ) : null}
    </>
  );
};
